/**
 * Pesquisa de Satisfação V2 — Renderização DINÂMICA baseada em template.
 *
 * Busca o template ativo do curso em /api/surveys/course/:courseId/template e
 * renderiza o formulário automaticamente, suportando os 5 tipos de pergunta:
 *   - rating5      → escala Ótimo/Bom/Regular/Ruim/Péssimo
 *   - nps10        → escala 0–10
 *   - yesno        → Sim/Não
 *   - multiselect  → checkboxes
 *   - text         → texto livre
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  CheckCircle2, ArrowRight, ArrowLeft, Sparkles,
} from "lucide-react";

const RATINGS = [
  { value: "otimo", label: "Ótimo", color: "bg-emerald-100 border-emerald-400 text-emerald-800" },
  { value: "bom", label: "Bom", color: "bg-sky-100 border-sky-400 text-sky-800" },
  { value: "regular", label: "Regular", color: "bg-amber-100 border-amber-400 text-amber-800" },
  { value: "ruim", label: "Ruim", color: "bg-orange-100 border-orange-400 text-orange-800" },
  { value: "pessimo", label: "Péssimo", color: "bg-rose-100 border-rose-400 text-rose-800" },
];

function RatingScale({ value, onChange, qid }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid={`q-${qid}`}>
      {RATINGS.map((r) => {
        const selected = value === r.value;
        return (
          <button
            type="button"
            key={r.value}
            onClick={() => onChange(r.value)}
            className={`h-12 rounded-lg border-2 text-xs font-medium transition-all ${
              selected
                ? r.color + " shadow-sm scale-[1.02]"
                : "bg-white border-[hsl(var(--border))] text-muted-foreground hover:border-[hsl(var(--accent))]"
            }`}
            data-testid={`q-${qid}-${r.value}`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function Nps10Scale({ value, onChange, qid }) {
  return (
    <div className="space-y-2" data-testid={`q-${qid}`}>
      <div className="grid grid-cols-11 gap-1">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
          const selected = value === n;
          const tone = n <= 6 ? "rose" : n <= 8 ? "amber" : "emerald";
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`h-11 rounded-md border-2 text-sm font-semibold transition-all ${
                selected
                  ? `bg-${tone}-100 border-${tone}-400 text-${tone}-800 shadow-sm scale-[1.05]`
                  : "bg-white border-[hsl(var(--border))] text-muted-foreground hover:border-[hsl(var(--accent))]"
              }`}
              data-testid={`q-${qid}-${n}`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>0 = de jeito nenhum</span>
        <span>10 = com certeza</span>
      </div>
    </div>
  );
}

function YesNo({ value, onChange, qid }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`q-${qid}`}>
      {[
        { v: true, label: "Sim" },
        { v: false, label: "Não" },
      ].map((opt) => {
        const selected = value === opt.v;
        return (
          <button
            key={String(opt.v)}
            type="button"
            onClick={() => onChange(opt.v)}
            className={`h-11 rounded-lg border-2 font-medium transition-all ${
              selected
                ? "bg-[hsl(var(--accent))]/15 border-[hsl(var(--accent))] text-[hsl(var(--accent))]"
                : "bg-white border-[hsl(var(--border))] text-muted-foreground hover:border-[hsl(var(--accent))]"
            }`}
            data-testid={`q-${qid}-${opt.v ? "yes" : "no"}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function MultiSelect({ options, value = [], onChange, qid }) {
  const toggle = (val) => {
    if (value.includes(val)) onChange(value.filter((v) => v !== val));
    else onChange([...value, val]);
  };
  return (
    <div className="space-y-2" data-testid={`q-${qid}`}>
      {(options || []).map((opt) => {
        const v = opt.value || opt.id;
        const checked = value.includes(v);
        return (
          <label
            key={v}
            className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
              checked
                ? "bg-[hsl(var(--accent))]/10 border-[hsl(var(--accent))]"
                : "bg-white border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]"
            }`}
            data-testid={`q-${qid}-${v}`}
          >
            <Checkbox checked={checked} onCheckedChange={() => toggle(v)} />
            <span className="text-sm">{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function TextField({ value, onChange, qid, q }) {
  const isLong = (q.max_length || 500) > 150;
  return isLong ? (
    <Textarea
      rows={4}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      maxLength={q.max_length || 500}
      placeholder={q.placeholder || ""}
      data-testid={`q-${qid}`}
    />
  ) : (
    <Input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      maxLength={q.max_length || 200}
      placeholder={q.placeholder || ""}
      data-testid={`q-${qid}`}
    />
  );
}

function QuestionField({ q, value, onChange }) {
  switch (q.type) {
    case "rating5":
      return <RatingScale qid={q.id} value={value} onChange={onChange} />;
    case "nps10":
      return <Nps10Scale qid={q.id} value={value} onChange={onChange} />;
    case "yesno":
      return <YesNo qid={q.id} value={value} onChange={onChange} />;
    case "multiselect":
      return <MultiSelect qid={q.id} options={q.options} value={value || []} onChange={onChange} />;
    case "text":
      return <TextField qid={q.id} q={q} value={value} onChange={onChange} />;
    default:
      return <p className="text-xs text-muted-foreground">[tipo de pergunta não suportado: {q.type}]</p>;
  }
}

function isAnswerEmpty(q, value) {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

export default function PesquisaSatisfacao() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [courseTitle, setCourseTitle] = useState("");
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(0); // índice de seção atual
  const [answers, setAnswers] = useState({}); // {questionId: value}

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [statusRes, tplRes] = await Promise.all([
          api.get(`/surveys/course/${courseId}/status`),
          api.get(`/surveys/course/${courseId}/template`),
        ]);
        if (!alive) return;
        if (statusRes.data?.completed) {
          setAlreadyAnswered(true);
        }
        setCourseTitle(statusRes.data?.course_title || tplRes.data?.course_title || "");
        setTemplate(tplRes.data?.template);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erro ao carregar pesquisa");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [courseId]);

  const sections = template?.sections || [];
  const currentSection = sections[step];
  const totalSteps = sections.length;
  const progressPct = totalSteps ? ((step + 1) / totalSteps) * 100 : 0;

  // Validação de seção: todas as perguntas required precisam estar respondidas
  const canAdvance = useMemo(() => {
    if (!currentSection) return false;
    return currentSection.questions.every((q) => {
      if (!q.required) return true;
      return !isAnswerEmpty(q, answers[q.id]);
    });
  }, [currentSection, answers]);

  const advance = () => {
    if (!canAdvance) {
      toast.warning("Responda as perguntas obrigatórias antes de avançar.");
      return;
    }
    if (step < totalSteps - 1) setStep(step + 1);
  };
  const back = () => { if (step > 0) setStep(step - 1); };

  const submit = async () => {
    // Validação final: todas as required preenchidas em todas as seções
    for (const sec of sections) {
      for (const q of sec.questions) {
        if (q.required && isAnswerEmpty(q, answers[q.id])) {
          toast.error(`Por favor responda: ${q.label}`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      await api.post(`/surveys/course/${courseId}/submit`, {
        template_id: template?.id,
        answers,
      });
      setSuccess(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar pesquisa");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="survey-loading">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (alreadyAnswered && !success) {
    return (
      <Card className="rounded-xl border-emerald-200 bg-emerald-50" data-testid="survey-already">
        <CardContent className="p-6 text-center space-y-3">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
          <h2 className="font-display font-semibold text-lg text-emerald-800">Você já respondeu esta pesquisa</h2>
          <p className="text-sm text-emerald-700">Obrigado pela sua contribuição! Seu certificado já está disponível.</p>
          <Button onClick={() => navigate("/home")} variant="outline" className="border-emerald-400">
            Voltar ao início
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (success) {
    return (
      <Card className="rounded-xl border-emerald-200 bg-emerald-50" data-testid="survey-success">
        <CardContent className="p-6 text-center space-y-3">
          <Sparkles className="w-12 h-12 text-emerald-600 mx-auto" />
          <h2 className="font-display font-semibold text-lg text-emerald-800">Pesquisa enviada com sucesso!</h2>
          <p className="text-sm text-emerald-700">Obrigado pela sua avaliação. Seu certificado foi liberado.</p>
          <div className="flex gap-2 justify-center pt-2">
            <Button onClick={() => navigate("/certificados")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="go-certificates">
              Ver meu certificado
            </Button>
            <Button onClick={() => navigate("/home")} variant="outline" className="border-emerald-400">
              Início
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!template || sections.length === 0) {
    return (
      <Card className="rounded-xl">
        <CardContent className="p-6 text-center space-y-3">
          <p className="text-sm text-muted-foreground">Nenhum modelo de pesquisa configurado para este curso.</p>
          <Link to="/home"><Button variant="outline">Voltar</Button></Link>
        </CardContent>
      </Card>
    );
  }

  const isLast = step === totalSteps - 1;

  return (
    <div className="space-y-4" data-testid="survey-page">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Pesquisa de satisfação</p>
            <h1 className="font-display font-semibold text-base text-[hsl(var(--primary))] line-clamp-2">{courseTitle}</h1>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            Etapa {step + 1} de {totalSteps}
          </div>
        </div>
        <Progress value={progressPct} className="h-1.5" />
      </div>

      {/* Seção atual */}
      <Card className="rounded-xl">
        <CardContent className="p-5 space-y-4">
          <div>
            <h2 className="font-display font-semibold text-base text-[hsl(var(--primary))]">{currentSection.title}</h2>
            {currentSection.description && (
              <p className="text-xs text-muted-foreground mt-0.5">{currentSection.description}</p>
            )}
          </div>

          <div className="space-y-5">
            {currentSection.questions.map((q) => (
              <div key={q.id} className="space-y-2">
                <label className="text-sm font-medium block">
                  {q.label}
                  {q.required && <span className="text-rose-500 ml-1">*</span>}
                </label>
                {q.help_text && <p className="text-[11px] text-muted-foreground -mt-1">{q.help_text}</p>}
                <QuestionField
                  q={q}
                  value={answers[q.id]}
                  onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Footer / Navegação */}
      <div className="flex gap-2 sticky bottom-[calc(80px+env(safe-area-inset-bottom))] bg-[hsl(var(--background))] pt-2 pb-1">
        <Button
          variant="outline"
          onClick={back}
          disabled={step === 0}
          className="flex-1"
          data-testid="survey-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
        {isLast ? (
          <Button
            onClick={submit}
            disabled={submitting || !canAdvance}
            className="flex-1"
            data-testid="survey-submit"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {submitting ? "Enviando…" : "Enviar pesquisa"}
          </Button>
        ) : (
          <Button
            onClick={advance}
            disabled={!canAdvance}
            className="flex-1"
            data-testid="survey-next"
          >
            Próxima <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
